import { FC, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataRoomAccessStatus, formatViewingTime, OTHER_ORGANISATION, RoomVisitor } from 'shared';

import { ChevronDownIcon } from '../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import { Search } from '../../../../components/search/index';
import { useTableSort } from '../../../../components/table/hooks/use-table-sort.hook';
import { Table, TableRow } from '../../../../components/table/index';
import { StringKey } from '../../../../lang/index';
import { Order } from '../../../../types/general.types';
import { PageRoute } from '../../../../types/page.types';
import { AccessStatusBadge } from '../../workspaces/components/access/access-badges';
import { LastActive } from '../../workspaces/components/last-active';
import { ActivitySection } from './activity-section';
import { FilterSelect } from './filter-select';
import { VisitorName } from './visitor-name';

enum Column {
  ACCESS = 'access',
  DOWNLOADS = 'downloads',
  LAST_ACTIVE = 'lastActive',
  NAME = 'name',
  OPEN = 'open',
  ORGANIZATION = 'organization',
  TIME_SPENT = 'timeSpent',
  TITLE = 'title',
  VIEWS = 'views',
}

type SortColumn = Exclude<Column, Column.OPEN>;

// 20.1.2 §11, in the order the story lists them, then the arrow that opens the visitor.
const COLUMN_ORDER = [
  Column.NAME,
  Column.ORGANIZATION,
  Column.TITLE,
  Column.LAST_ACTIVE,
  Column.VIEWS,
  Column.TIME_SPENT,
  Column.DOWNLOADS,
  Column.ACCESS,
  Column.OPEN,
];

const COLUMN_TITLE: Record<SortColumn, StringKey> = {
  [Column.ACCESS]: 'ACCESS',
  [Column.DOWNLOADS]: 'DOWNLOADS',
  [Column.LAST_ACTIVE]: 'LAST_ACTIVE',
  [Column.NAME]: 'NAME',
  [Column.ORGANIZATION]: 'ORGANIZATION',
  [Column.TIME_SPENT]: 'TIME_SPENT',
  [Column.TITLE]: 'TITLE',
  [Column.VIEWS]: 'VIEWS',
};

const SORT_COLUMNS = Object.keys(COLUMN_TITLE) as SortColumn[];

// What each column sorts by; text compares as text, everything else as a number.
const SORT_VALUE: Record<SortColumn, (visitor: RoomVisitor) => number | string> = {
  [Column.ACCESS]: ({ accessStatus }) => accessStatus ?? '',
  [Column.DOWNLOADS]: ({ downloads }) => downloads,
  [Column.LAST_ACTIVE]: ({ lastActiveAt }) => (lastActiveAt ? Date.parse(lastActiveAt) : 0),
  [Column.NAME]: ({ name }) => name,
  [Column.ORGANIZATION]: ({ organisation }) => organisation,
  [Column.TIME_SPENT]: ({ viewingTimeMs }) => viewingTimeMs,
  [Column.TITLE]: ({ title }) => title ?? '',
  [Column.VIEWS]: ({ views }) => views,
};

/**
 * 20.1.0 §10's status, from the live grant. A visitor whose access was withdrawn keeps the history
 * they made, so the row stays and only this cell says what became of them; a grant deleted outright
 * leaves nothing to report.
 */
const AccessCell: FC<{ status: DataRoomAccessStatus | null }> = ({ status }) =>
  status ? <AccessStatusBadge status={status} /> : <span className="text-gray-400">–</span>;

/**
 * 20.1.2 §11, with the organisation filter the story asks for.
 *
 * Organisations are derived from the address by the shared rule, so everything the rule cannot
 * resolve — a generic provider, a malformed address — lands in the single "Other" bucket rather
 * than becoming an organisation of its own. Search, filter and sort work on the page the room
 * returned; the Visitors tab is where the whole audience is read.
 */
export const RoomVisitorTable: FC<{
  /** How many the room has in the window, of which `visitors` is the first page. */
  total: number;
  visitors: RoomVisitor[];
}> = ({ total, visitors }) => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [organisation, setOrganisation] = useState('');
  const [sortTitles, sort, ariaSort] = useTableSort<SortColumn>(SORT_COLUMNS, COLUMN_TITLE);

  const organisations = useMemo(
    () =>
      [...new Set(visitors.map((visitor) => visitor.organisation))].sort((left, right) =>
        left === OTHER_ORGANISATION
          ? 1
          : right === OTHER_ORGANISATION
            ? -1
            : left.localeCompare(right),
      ),
    [visitors],
  );
  // A choice made in another window may have nobody in this one; it then filters nothing, so the
  // button and the rows agree on "All Organizations" instead of a hidden filter emptying the table.
  const activeOrganisation = organisations.includes(organisation) ? organisation : '';

  const rows = useMemo(() => {
    const query = search.trim().toLowerCase();
    const matching = visitors.filter(
      (visitor) =>
        (!activeOrganisation || visitor.organisation === activeOrganisation) &&
        (!query || visitor.name.toLowerCase().includes(query)),
    );

    if (!sort.field || !sort.order) return matching;

    const valueOf = SORT_VALUE[sort.field];
    const direction = sort.order === Order.ASC ? 1 : -1;

    return [...matching].sort((left, right) => {
      const a = valueOf(left);
      const b = valueOf(right);

      return direction * (typeof a === 'string' ? a.localeCompare(String(b)) : a - Number(b));
    });
  }, [activeOrganisation, search, sort, visitors]);

  return (
    <ActivitySection
      actions={
        visitors.length > 0 && (
          <div className="flex items-center gap-3">
            <Search buttonClassName="bg-white shadow-xs" callback={setSearch} />
            <FilterSelect
              allLabel="ALL_ORGANIZATIONS"
              onChange={setOrganisation}
              options={organisations.map((name) => ({ label: name, value: name }))}
              value={activeOrganisation}
            />
          </div>
        )
      }
      title="VISITOR_ACTIVITY"
    >
      {!visitors.length ? (
        <span className="px-4 py-6 text-sm font-[450] text-gray-400">
          <AppFormattedMessage id="NO_ACTIVITY_YET" />
        </span>
      ) : rows.length ? (
        <div className="overflow-x-auto">
          <Table<Column>
            ariaSort={ariaSort}
            className="table-fixed [&_tbody]:divide-gray-100"
            columnOrder={COLUMN_ORDER}
            columnsTitle={{ ...sortTitles, [Column.OPEN]: null }}
            tHeadClassNames={{
              // The sort hook writes its titles at 450; the design's headers are 500, as on every
              // other Activity table.
              labelClassName: '[&_span]:font-medium',
              thClassName: {
                general: 'px-4',
                [Column.ACCESS]: 'w-24',
                [Column.DOWNLOADS]: 'w-[116px]',
                [Column.LAST_ACTIVE]: 'w-[145px]',
                [Column.NAME]: 'w-[210px]',
                [Column.OPEN]: 'w-12',
                [Column.ORGANIZATION]: 'w-[154px]',
                [Column.TIME_SPENT]: 'w-[145px]',
                [Column.TITLE]: 'w-[86px]',
                [Column.VIEWS]: 'w-[74px]',
              },
            }}
          >
            {rows.map((visitor) => (
              <TableRow
                columnOrder={COLUMN_ORDER}
                contentClassName={{ general: 'gap-2 px-4', [Column.OPEN]: 'justify-center px-2' }}
                fields={{
                  id: visitor.email,
                  [Column.NAME]: <VisitorName name={visitor.name} />,
                  [Column.ORGANIZATION]: visitor.organisation,
                  // Nothing writes a title yet; a dash is the honest answer until something does.
                  [Column.TITLE]: visitor.title ?? '–',
                  [Column.LAST_ACTIVE]: <LastActive at={visitor.lastActiveAt} />,
                  [Column.VIEWS]: visitor.views,
                  [Column.TIME_SPENT]: formatViewingTime(visitor.viewingTimeMs),
                  [Column.DOWNLOADS]: visitor.downloads,
                  [Column.ACCESS]: <AccessCell status={visitor.accessStatus} />,
                  [Column.OPEN]: <ChevronDownIcon className="-rotate-90" iconColor="#475467" />,
                }}
                key={visitor.email}
                labelClassName="min-w-0 text-sm"
                onClick={() =>
                  navigate(
                    PageRoute.DATA_ROOM_ACTIVITY_VISITOR.replace(':visitorKey', visitor.visitorKey),
                  )
                }
                tdClassName="p-0"
                trClassName="cursor-pointer transition-colors duration-150 hover:bg-gray-50"
              />
            ))}
          </Table>
        </div>
      ) : (
        <span className="px-4 py-6 text-sm font-[450] text-gray-400">
          <AppFormattedMessage id="NO_RESULTS_FOUND" />
        </span>
      )}
      {/*
        The panel shows a page, not the list: a popular room's visitors are unbounded, and the
        Visitors tab is where the whole audience is read with search, filters and sorting.
      */}
      {total > visitors.length && (
        <span className="px-4 py-2 text-label-sm text-gray-500">
          <AppFormattedMessage id="SHOWING_FIRST_OF_VISITORS" />
          {` (${visitors.length}/${total})`}
        </span>
      )}
    </ActivitySection>
  );
};
